import { plainMd } from "./markdown-view";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { InboxItem } from "@foreman/app-core";
import type { Nav } from "./nav";
import { agentKey, agentLabel } from "./ask-card";
import { chatOnScreen } from "./chat-focus";
import { isAsk } from "./sessions-feed";
import { AgentLogo, Spinner, useApp } from "./ui";

const SHOW_MS = 10_000; // how long the pop-up stays, unless a finger is on it
const FRESH_MS = 90_000; // an ask older than this is not news: it was waiting before the app opened or reconnected

/**
 * When an agent starts waiting for you, a card drops in from the top of whatever screen you are on, like a chat message
 * arriving: who is asking, what for, and Approve / Deny right there. Tap the card to open the Inbox; swipe it up, or wait,
 * to dismiss it. It stays quiet where the ask is already in front of you (Home lists it at the top, the Inbox is the list),
 * and for anything that was already waiting when the app opened.
 */
export function HeadsUp({ nav }: { nav: Nav }) {
  const { hub } = useApp();
  const items = useSyncExternalStore(hub.subscribe, hub.getInbox);
  const [queue, setQueue] = useState<string[]>([]); // ids to show, newest last
  const seen = useRef(new Set<string>());
  const view = useRef(nav.view.name);
  view.current = nav.view.name;
  const [busy, setBusy] = useState<null | "approve" | "deny">(null);
  const [held, setHeld] = useState(false);
  const touch = useRef<number | null>(null);

  const open = items.filter((i) => isAsk(i.event));

  // Something new is waiting: queue it, unless the person is already looking at where it is listed.
  useEffect(() => {
    const fresh: string[] = [];
    for (const a of open) {
      if (seen.current.has(a.event.id)) continue;
      seen.current.add(a.event.id);
      const here = view.current === "inbox" || view.current === "home" || chatOnScreen(a.event.sessionId); // the chat shows its own card
      if (!here && Date.now() - a.event.createdAt < FRESH_MS) fresh.push(a.event.id);
    }
    if (fresh.length) {
      setQueue((q) => [...q, ...fresh]);
      try {
        navigator.vibrate?.(30);
      } catch {
        /* no haptics here */
      }
    }
    // answered somewhere else (the computer, another phone): drop it from the queue
    setQueue((q) => {
      const live = q.filter((id) => open.some((a) => a.event.id === id));
      return live.length === q.length ? q : live;
    });
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const item: InboxItem | undefined = queue.length ? open.find((a) => a.event.id === queue[queue.length - 1]) : undefined;
  const id = item?.event.id;

  const dismiss = (which: string) => setQueue((q) => q.filter((x) => x !== which));

  // Goes away on its own, but not while it is being touched or hovered.
  useEffect(() => {
    if (!id || held) return;
    const t = setTimeout(() => dismiss(id), SHOW_MS);
    return () => clearTimeout(t);
  }, [id, held]);

  // Landing on the Inbox or Home means the ask is in front of you now.
  useEffect(() => {
    if (id && (nav.view.name === "inbox" || nav.view.name === "home")) dismiss(id);
  }, [nav.view.name, id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!item || !id) return null;
  const { event, computer } = item;
  const more = queue.length - 1;
  const detail = event.kind === "approval" ? event.detail?.split("\n").find((l) => l.trim()) : event.questions?.[0] ? plainMd(event.questions[0].question) : undefined;

  const respond = async (action: "approve" | "deny") => {
    const conn = hub.get(computer.id);
    if (!conn || busy) return;
    setBusy(action);
    try {
      await conn.respondInbox(id, action);
      dismiss(id);
    } catch {
      /* stays up: the Inbox shows the reason if it keeps failing */
    }
    setBusy(null);
  };

  return (
    <div
      className="headsup"
      role="alert"
      aria-live="assertive"
      data-testid="heads-up"
      data-event={id}
      onPointerEnter={() => setHeld(true)}
      onPointerLeave={() => setHeld(false)}
      onTouchStart={(e) => {
        setHeld(true);
        touch.current = e.touches[0]!.clientY;
      }}
      onTouchEnd={(e) => {
        setHeld(false);
        if (touch.current !== null && e.changedTouches[0]!.clientY - touch.current < -28) dismiss(id); // swiped up
        touch.current = null;
      }}
    >
      <button
        className="hu-main"
        onClick={() => {
          dismiss(id);
          nav.goTab({ name: "inbox" });
        }}
      >
        <span className="hu-ico" aria-hidden>
          <AgentLogo agent={agentKey(event.agent)} size={22} />
        </span>
        <span className="hu-text">
          <span className="hu-top">
            {agentLabel(event.agent)} · {computer.label}
            <em>{event.kind === "approval" ? "needs approval" : "has a question"}</em>
          </span>
          <span className="hu-title">{event.title}</span>
          {detail && <span className="hu-detail">{detail}</span>}
        </span>
      </button>
      <div className="hu-actions">
        {event.kind === "approval" ? (
          <>
            <button className="btn secondary sm" disabled={!!busy} onClick={() => void respond("deny")}>
              {busy === "deny" && <Spinner />}Deny
            </button>
            <button className="btn primary sm" disabled={!!busy} onClick={() => void respond("approve")}>
              {busy === "approve" && <Spinner />}Approve
            </button>
          </>
        ) : (
          <button
            className="btn primary sm"
            onClick={() => {
              dismiss(id);
              nav.goTab({ name: "inbox" });
            }}
          >
            Answer
          </button>
        )}
        {more > 0 && <span className="hu-more">+{more} more</span>}
        <button className="hu-x" aria-label="Dismiss" onClick={() => dismiss(id)}>
          ×
        </button>
      </div>
    </div>
  );
}
